(function () {
  /**
   * CONSTANTS & EVENT NAMES
   */
  const EVENTS = {
    DISCOUNT_APPLY: 'discount:apply',
    DISCOUNT_REMOVE: 'discount:remove',
    DISCOUNT_CLEAR: 'discount:clear',
    STATE_CHANGED: 'discount:state:changed',
    SHOW_ERROR: 'discount:error',
    SHOW_SUCCESS: 'discount:success',
    OVERLAY_SET: 'overlay:set'
  };

  const OVERLAYS = {
    DROPDOWN: 'dropdown',
    POPUP_GIFT: 'popup_gift',
    POPUP_DISCOUNT: 'popup_discount',
    APPLY_TAG: 'apply_tag'
  };

  /**
   * STORE: Single Source of Truth for state
   */
  class DiscountStore {
    constructor() {
      this.state = {
        cart: null,
        loading: false,
        error: null,
        message: null,
        status: 'idle', // idle, loading, conflict, success, error
        appliedDiscounts: [],
        activeOverlay: null,
        popupData: null,
      };
      this.subscribers = [];
    }

    static getInstance() {
      if (!window.discountStore) {
        window.discountStore = new DiscountStore();
      }
      return window.discountStore;
    }

    update(newState) {
      this.state = { ...this.state, ...newState };
      this.notify();
    }

    subscribe(callback) {
      this.subscribers.push(callback);
      // Optional: immediate call with current state
      callback(this.state);
    }

    notify() {
      this.subscribers.forEach(callback => callback(this.state));
      document.dispatchEvent(new CustomEvent(EVENTS.STATE_CHANGED, { detail: this.state }));
    }

    getState() {
      return this.state;
    }
  }

  /**
   * CAMPAIGN MANAGER: Logic for Gifts & Strategy
   */
  class CampaignManager {
    constructor(availableGifts = []) {
      this.availableGifts = availableGifts;
    }

    findConflicts(cartItems) {
      if (!this.availableGifts || Object.keys(this.availableGifts).length === 0) return [];

      const giftsInCart = [];
      cartItems.forEach(item => {
        const variantId = item.variant_id;
        if (variantId in this.availableGifts) {
          giftsInCart.push(variantId);
        }
      });
      return giftsInCart;
    }

    prepareRemovalUpdates(itemsToRemove, cartItems) {
      const updates = {};
      cartItems.forEach(item => {
        if (itemsToRemove.includes(item.variant_id)) {
          updates[item.variant_id] = 0;
        }
      });
      return updates;
    }

    /**
     * Prepare updates to switch gifts by clearing all current gifts 
     * and setting the target gift quantity.
     */
    prepareReplacementUpdates(targetVariantId, cartItems) {
      const updates = {};

      // 1. Identify all current gift variants (including the target)
      const giftVariantIds = Object.keys(this.availableGifts).map(id => parseInt(id));

      // 2. Set all existing gifts in the cart to 0
      cartItems.forEach(item => {
        if (giftVariantIds.includes(item.variant_id)) {
          updates[item.variant_id] = 0;
        }
      });

      // 3. Add the target gift (quantity 1)
      updates[targetVariantId] = 1;

      return updates;
    }
  }

  /**
   * MODEL: API Interactions
   */
  class DiscountModel {
    constructor(marketplace) {
      this.marketplace = marketplace;
    }

    async applyDiscountCode(cartId, code) {
      let body = {
        "cartId": `gid://shopify/Cart/${cartId}`,
        "discountCodes": code
      };

      try {
        const response = await fetch(`https://storefront-shopify.slimcase.vn/${this.marketplace}/api/2025-02/shopify/cart/discount.json`, {
          method: 'POST',
          headers: {
            'Accept': '*/*',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        });
        return await response.json();
      } catch (error) {
        console.error('Error applying discount:', error);
        return { error: true };
      }
    }

    async fetchCart() {
      return await (await fetch(`${Shopify.routes.root}cart.js`)).json();
    }

    async checkAvailability(code) {
      if (!code) return { available: false, message: "Invalid discount code." };

      try {
        const response = await fetch(`https://storefront-shopify.slimcase.vn/${this.marketplace}/api/2025-09/shopify/discount/available.json`, {
          method: 'POST',
          headers: {
            'Accept': '*/*',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ "discount_code": code }),
        });
        return await response.json();
      } catch (error) {
        console.error('Error checking availability:', error);
        return { available: false, message: "Server error. Please try again." };
      }
    }

    async updateCart(data) {
      return await fetch(window.Shopify.routes.root + 'cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(data)
      }).then(response => response.json());
    }
  }

  /**
   * CONTROLLER: Orchestrator
   */
  class DiscountController {
    constructor(model, store, campaignManager) {
      this.model = model;
      this.store = store;
      this.campaignManager = campaignManager;
    }

    async apply(code) {
      this.store.update({ loading: true, status: 'loading', error: null, message: null });

      const availability = await this.model.checkAvailability(code);
      if (!availability.available) {
        this.store.update({
          loading: false,
          status: 'error',
          error: availability.message || "Kode promo tidak berlaku atau ada promo lain yang sudah digunakan"
        });
        return;
      }

      const cart = await this.model.fetchCart();
      const conflicts = this.campaignManager.findConflicts(cart.items);

      if (conflicts.length > 0) {
        this.store.update({
          loading: false,
          status: 'conflict',
          activeOverlay: OVERLAYS.POPUP_GIFT,
          popupData: { code, conflicts, cartId: cart.token, items: cart.items }
        });
        return;
      }

      const result = await this.model.applyDiscountCode(cart.token, code);
      this._handleResponse(result);
    }

    async remove(code) {
      // 1. Show the popup immediately to prevent UI lag
      this.store.update({
        activeOverlay: OVERLAYS.POPUP_DISCOUNT,
        popupData: { code }
      });

      // 2. Run backend process (fetch cart) asynchronously
      const cart = await this.model.fetchCart();

      // 3. Update state with backend data once resolved
      if (this.store.getState().activeOverlay === OVERLAYS.POPUP_DISCOUNT) {
        this.store.update({
          popupData: { code, cartId: cart.token }
        });
      }
    }

    async resolveConflict(data) {
      const { code, conflicts, items } = data;
      this.store.update({ loading: true, status: 'loading' });

      const updates = this.campaignManager.prepareRemovalUpdates(conflicts, items);
      await this.model.updateCart({ updates, discount: code });

      this.store.update({ loading: false, status: 'success', message: "Diskon berhasil diterapkan" });
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    }

    async executeCancel() {
      this.store.update({ loading: true, status: 'loading' });
      await this.model.updateCart({ discount: "" });
      this.store.update({ loading: false, status: 'success', message: "Translation missing: id.cart.general.discount_removed" });
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    }

    _handleResponse(result) {
      if (result.errors || result.error) {
        this.store.update({ loading: false, status: 'error', error: "Kode promo tidak berlaku atau ada promo lain yang sudah digunakan" });
      } else {
        this.store.update({ loading: false, status: 'success', message: "Diskon berhasil diterapkan" });
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      }
    }
  }

  /**
   * UI MANAGER: Centralized DOM manipulation and debouncing
   */
  class DiscountUIManager {
    constructor(store) {
      this.store = store;
      this.pendingRender = false;
      this.store.subscribe(() => this.scheduleRender());
    }

    scheduleRender() {
      if (this.pendingRender) return;
      this.pendingRender = true;
      requestAnimationFrame(() => {
        this.render(this.store.getState());
        this.pendingRender = false;
      });
    }

    render(state) {
      const applyTag = document.querySelector('apply-discount');
      const discountNote = document.querySelector('discount-note');
      const popupGift = document.querySelector('popup-remove-gift');
      const popupDiscount = document.querySelector('popup-remove-discount');

      if (popupGift && typeof popupGift.render === 'function') {
        popupGift.render(state);
      }

      if (popupDiscount && typeof popupDiscount.render === 'function') {
        popupDiscount.render(state);
      }

      if (applyTag && typeof applyTag.render === 'function') {
        applyTag.render(state);
      }
      if (discountNote && typeof discountNote.render === 'function') {
        discountNote.render(state);
      }

    }
  }

  /**
   * VIEW: Custom Elements
   */

  // 1. Root Element: Apply Discount Input & Button
  class ApplyDiscountTag extends HTMLElement {
    constructor() {
      super();
      this.store = DiscountStore.getInstance();

    }

    connectedCallback() {
      this.addEventListener('click', this._handleClick.bind(this));
    }
    show() {
      this.classList.remove('hide')
    }

    hide() {
      this.classList.add('hide')
    }

    _handleClick(event) {
      if (event.target && (event.target.classList.contains('text--apply--discount') || event.target.closest('.apply-discount'))) {
        const input = this.querySelector('#discount-code');
        const code = input ? input.value.trim() : '';
        if (code) {
          document.dispatchEvent(new CustomEvent(EVENTS.DISCOUNT_APPLY, { detail: { code } }));
        }
      }
      if (event.target && event.target.classList.contains('dropdown-discount') || event.target.closest('.dropdown-discount')) {
        this._toggleDropdown();
        return;
      }
    }

    _toggleDropdown() {
      const currentOverlay = this.store.getState().activeOverlay;
      const isDropdownActive = currentOverlay === OVERLAYS.DROPDOWN;
      this.store.update({ activeOverlay: isDropdownActive ? null : OVERLAYS.DROPDOWN });
    }

    render(state) {
      const btn = this.querySelector('#apply-discount-btn');
      const text = this.querySelector('.text--apply--discount');
      const loading = this.querySelector('.loading');
      const input = this.querySelector('#discount-code');
      const dropdownBtn = this.querySelector('.dropdown-discount');

      if (!btn || !text || !loading) return;

      // Handle Main Apply Button
      if (state.loading) {
        btn.setAttribute('disabled', '');
        text.classList.add('hide');
        loading.classList.remove('hide');
      } else {
        btn.removeAttribute('disabled');
        text.classList.remove('hide');
        loading.classList.add('hide');
        if (state.status === 'success' && input) input.value = '';
      }

      // Handle Dropdown Button State
      if (dropdownBtn) {
        if (state.activeOverlay === OVERLAYS.DROPDOWN) {
          dropdownBtn.classList.add('pressed');
        } else {
          dropdownBtn.classList.remove('pressed');
        }
      }

      // Handle Overlay State
      if (state.activeOverlay === OVERLAYS.POPUP_GIFT || state.activeOverlay === OVERLAYS.POPUP_DISCOUNT) {
        this.hide();
      } else {
        this.show();
      }
    }
  }

  // 2. Note Element: Error/Success messages
  class DiscountNote extends HTMLElement {
    constructor() {
      super();
      this.store = DiscountStore.getInstance();
    }

    connectedCallback() {
      this.addEventListener('click', (event) => {
        if (event.target.closest('.close-discount-slide-cart')) {
          this.hide();
        }
      });
    }

    render(state) {
      if (state.status === 'error' && state.error) {
        this.show(state.error, 'error');
      } else if (state.status === 'success' && state.message) {
        this.show(state.message, 'success');
      } else if (state.status === 'loading' || state.status === 'idle') {
        this.hide();
      }

      if (state.activeOverlay === OVERLAYS.DROPDOWN) {
        this.hide();
      }
      if (state.activeOverlay === OVERLAYS.POPUP_GIFT) {
        this.hide();
      }
      if (state.activeOverlay === OVERLAYS.POPUP_DISCOUNT) {
        this.hide();
      }
    }

    hide() {
      this.store.update({ status: 'idle', message: '', error: '' });
      this.style.display = 'none';
      this.classList.remove('note--init', 'note--success', 'note--fail');
    }

    show(message, type) {
      const textEl = this.querySelector('.notice__text');
      if (textEl) textEl.innerHTML = message;

      this.classList.remove('note--init', 'note--success', 'note--fail');
      this.classList.add(type === 'success' ? 'note--success' : 'note--fail');
      this.style.display = 'block';
    }
  }

  // 3. Discount Tags: List of applied codes
  class TagCodeDiscount extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.addEventListener('click', this._handleRemove.bind(this));
    }

    _handleRemove(event) {
      const target = event.target;
      const tag = target.closest('.discount-tag');
      if (tag) {
        const code = tag.getAttribute('data-discount-id');
        if (code) {
          document.dispatchEvent(new CustomEvent(EVENTS.DISCOUNT_REMOVE, { detail: { code } }));
        }
      }
    }
  }

  // 4. Popups: Gift conflict & Removal Confirmation
  class PopupBase extends HTMLElement {
    constructor() {
      super();
      this.classList.add('close-content');
      this.overlayId = null; // To be set by subclasses
      this.store = DiscountStore.getInstance();
    }

    render(state) {
      if (state.activeOverlay === this.overlayId) {
        this.show(state.popupData);
      } else {
        this.hide();
      }
    }

    show(data) {
      this.classList.remove('close-content');
      this.classList.add('open-content');
      this.data = data;
    }

    hide() {
      this.classList.remove('open-content');
      this.classList.add('close-content');
    }
  }

  class PopupRemoveGift extends PopupBase {
    constructor() {
      super();
      this.overlayId = OVERLAYS.POPUP_GIFT;
      this.addEventListener('click', this._handleClick.bind(this));
    }

    _handleClick(event) {
      const target = event.target;
      if (target.classList.contains('confirm-remove-gift')) {
        document.dispatchEvent(new CustomEvent(EVENTS.DISCOUNT_CLEAR));
        controller.resolveConflict(this.data);
        this.store.update({ activeOverlay: null });
      } else if (target.classList.contains('close-popup-gift') || target.closest('.close-popup-gift') || target.classList.contains('btn--secondary')) {
        this.store.update({ activeOverlay: null, loading: false, status: 'idle' });
      }
    }
  }

  class PopupRemoveDiscount extends PopupBase {
    constructor() {
      super();
      this.overlayId = OVERLAYS.POPUP_DISCOUNT;
      this.addEventListener('click', this._handleClick.bind(this));
    }

    _handleClick(event) {
      const target = event.target;
      if (target.classList.contains('confirm-remove-discount')) {
        controller.executeCancel();
        this.store.update({ activeOverlay: null });
      } else if (target.classList.contains('close-popup-discount') || target.closest('.close-popup-discount') || target.classList.contains('btn--secondary')) {
        this.store.update({ activeOverlay: null, loading: false, status: 'idle' });
      }
    }
  }

  /**
   * INITIALIZATION
   */
  if (!window.customElements.get('apply-discount')) {
    window.customElements.define('apply-discount', ApplyDiscountTag);
  }
  if (!window.customElements.get('discount-note')) {
    window.customElements.define('discount-note', DiscountNote);
  }
  if (!window.customElements.get('tag-code-discount')) {
    window.customElements.define('tag-code-discount', TagCodeDiscount);
  }
  if (!window.customElements.get('popup-remove-gift')) {
    window.customElements.define('popup-remove-gift', PopupRemoveGift);
  }
  if (!window.customElements.get('popup-remove-discount')) {
    window.customElements.define('popup-remove-discount', PopupRemoveDiscount);
  }

  // Global Controller Setup
  const container = document.querySelector('apply-discount');
  const marketplace = container?.dataset.marketplace || 'indo';
  const model = new DiscountModel(marketplace);
  const store = DiscountStore.getInstance();
  const campaign = new CampaignManager(window.list_gifts || []);
  const controller = new DiscountController(model, store, campaign);
  const uiManager = new DiscountUIManager(store);

  // Hook up global listeners for the Controller
  document.addEventListener(EVENTS.DISCOUNT_APPLY, (e) => controller.apply(e.detail.code));
  document.addEventListener(EVENTS.DISCOUNT_REMOVE, (e) => controller.remove(e.detail.code));

})();